import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  adminListTopics,
  adminAddTopic,
  adminDeleteTopic,
  adminGenerateDraft,
} from "@/lib/blog-admin.functions";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Trash2, Sparkles, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/blog-topics")({
  component: BlogTopicsPage,
  head: () => ({ meta: [{ title: "Blog topics · Admin" }, { name: "robots", content: "noindex" }] }),
});

function BlogTopicsPage() {
  const list = useServerFn(adminListTopics);
  const add = useServerFn(adminAddTopic);
  const del = useServerFn(adminDeleteTopic);
  const gen = useServerFn(adminGenerateDraft);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [priority, setPriority] = useState(50);
  const [busy, setBusy] = useState<string | null>(null);

  const q = useQuery({ queryKey: ["admin-blog-topics"], queryFn: () => list() });
  const topics = Array.isArray(q.data) ? (q.data as any[]) : [];

  const submit = async () => {
    if (!topic.trim()) return toast.error("Topic is required");
    setBusy("add");
    try {
      await add({ data: { topic, category: category || null, notes: notes || null, priority } });
      setTopic(""); setCategory(""); setNotes(""); setPriority(50);
      await qc.invalidateQueries({ queryKey: ["admin-blog-topics"] });
      toast.success("Topic queued");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    } finally { setBusy(null); }
  };

  const runGen = async (id: string) => {
    setBusy(id);
    try {
      const r = (await gen({ data: { topicId: id, generateHero: true } })) as any;
      await qc.invalidateQueries({ queryKey: ["admin-blog-topics"] });
      toast.success("Draft generated");
      if (r?.post?.id) void navigate({ to: "/admin/posts/$id", params: { id: r.post.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Generation failed");
    } finally { setBusy(null); }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this topic?")) return;
    await del({ data: { id } });
    await qc.invalidateQueries({ queryKey: ["admin-blog-topics"] });
  };

  return (
    <div>
      <BlogSubNav />
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div>
          <h1 className="font-display text-2xl font-bold">Topic queue</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The AI agent will draft the highest-priority queued topic next.
          </p>

          <div className="mt-6 overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Topic</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {q.isLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
                ) : topics.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No topics queued.</td></tr>
                ) : topics.map((t) => (
                  <tr key={t.id} className="border-t border-border align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium">{t.topic}</div>
                      {t.notes && <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{t.notes}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{t.category ?? "—"}</td>
                    <td className="px-4 py-3 text-xs">{t.priority}</td>
                    <td className="px-4 py-3 text-xs capitalize">{t.status}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <Button size="sm" variant="outline" disabled={busy === t.id} onClick={() => runGen(t.id)}>
                          {busy === t.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Sparkles className="mr-1 h-3 w-3" />}
                          Generate
                        </Button>
                        <button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-destructive" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Add topic</p>
          <Label htmlFor="t-topic">Topic</Label>
          <Input id="t-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. DSCR loans for Florida rentals 2026" />
          <Label htmlFor="t-cat" className="mt-3 block">Category</Label>
          <Input id="t-cat" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="DSCR Basics" />
          <Label htmlFor="t-notes" className="mt-3 block">Notes / angle</Label>
          <Textarea id="t-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Label htmlFor="t-pri" className="mt-3 block">Priority (0–100)</Label>
          <Input id="t-pri" type="number" min={0} max={100} value={priority} onChange={(e) => setPriority(Number(e.target.value) || 0)} />
          <Button className="mt-4 w-full" disabled={busy === "add"} onClick={submit}>
            {busy === "add" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Queue topic
          </Button>
        </aside>
      </div>
    </div>
  );
}
